import axiosServices from '../axios';

export interface OrganizationTimezoneResponse {
  timezone: string;
  timezoneId: string;
}

export interface OrganizationTimezoneError {
  message: string;
  code?: string;
  timestamp: Date;
}

/**
 * Service for fetching organization default timezone
 */
export class OrganizationTimezoneService {
  private static instance: OrganizationTimezoneService;
  private readonly apiUrl: string;

  private constructor() {
    const { VITE_API_BASE_URL } = import.meta.env;
    if (!VITE_API_BASE_URL) {
      throw new Error('Missing environment variable: VITE_API_BASE_URL');
    }
    this.apiUrl = `${VITE_API_BASE_URL}/api/v3/global-settings/default-timezone`;
  }

  public static getInstance(): OrganizationTimezoneService {
    if (!OrganizationTimezoneService.instance) {
      OrganizationTimezoneService.instance = new OrganizationTimezoneService();
    }
    return OrganizationTimezoneService.instance;
  }

  /**
   * Fetch the organization's default timezone
   * This should be called after successful login
   */
  public async fetchOrganizationDefaultTimezone(): Promise<OrganizationTimezoneResponse> {
    try {
      const response = await axiosServices.get<OrganizationTimezoneResponse>(this.apiUrl);

      // Validate response data
      if (!response.data || !response.data.timezone) {
        throw new Error('Invalid response format: missing timezone data');
      }

      return {
        timezone: response.data.timezone,
        timezoneId: response.data.timezoneId || response.data.timezone
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const organizationTimezoneError: OrganizationTimezoneError = {
        message: `Failed to fetch organization default timezone: ${errorMessage}`,
        code: error instanceof Error && 'code' in error ? String(error.code) : undefined,
        timestamp: new Date()
      };

      // Log the error for debugging
      console.error('OrganizationTimezoneService Error:', organizationTimezoneError);

      throw organizationTimezoneError;
    }
  }

  /**
   * Store organization default timezone in localStorage
   */
  public storeOrganizationDefaultTimezone(timezone: string): void {
    try {
      localStorage.setItem('orgDefaultTimezone', timezone);
    } catch (error) {
      console.warn('Failed to store organization default timezone in localStorage:', error);
    }
  }

  /**
   * Get organization default timezone from localStorage
   */
  public getOrganizationDefaultTimezone(): string | null {
    try {
      return localStorage.getItem('orgDefaultTimezone');
    } catch (error) {
      console.warn('Failed to get organization default timezone from localStorage:', error);
      return null;
    }
  }

  /**
   * Clear organization default timezone from localStorage
   */
  public clearOrganizationDefaultTimezone(): void {
    try {
      localStorage.removeItem('orgDefaultTimezone');
    } catch (error) {
      console.warn('Failed to clear organization default timezone from localStorage:', error);
    }
  }

  /**
   * Get a fallback timezone when API fails
   * Returns browser timezone or a sensible default
   */
  public getFallbackTimezone(): string {
    try {
      // Try to get browser timezone first
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTimezone) {
        return browserTimezone;
      }
    } catch (error) {
      console.warn('Failed to detect browser timezone:', error);
    }

    // Fallback to a common timezone (Central Time as mentioned in requirements)
    return 'America/Chicago';
  }

  /**
   * Fetch organization timezone with fallback
   * Returns organization timezone if available, otherwise falls back to browser/default
   */
  public async fetchOrganizationDefaultTimezoneWithFallback(): Promise<OrganizationTimezoneResponse> {
    try {
      return await this.fetchOrganizationDefaultTimezone();
    } catch (error) {
      console.warn('Failed to fetch organization timezone, using fallback:', error);
      const fallbackTimezone = this.getFallbackTimezone();

      return {
        timezone: fallbackTimezone,
        timezoneId: fallbackTimezone
      };
    }
  }
}

export const organizationTimezoneService = OrganizationTimezoneService.getInstance();
